import { getProfilePicDownloadURL, profilePicCache } from '@dao/UsersDAO';

const SESSION_PROFILE_IMAGE_KEY = 'v2.profile-image-cache';

type SessionProfileImageEntry = {
  userId: number | string | null;
  s3Key: string | null;
  dataUrl: string | null;
};

const inMemoryProfileImageEntry: SessionProfileImageEntry = {
  userId: null,
  s3Key: null,
  dataUrl: null,
};

const isBrowser = () => typeof window !== 'undefined';

const syncLegacyProfilePicCache = (entry: SessionProfileImageEntry) => {
  profilePicCache.s3Key = entry.s3Key;
  profilePicCache.resolvedUrl = entry.dataUrl;
};

const setInMemoryEntry = (entry: SessionProfileImageEntry) => {
  inMemoryProfileImageEntry.userId = entry.userId;
  inMemoryProfileImageEntry.s3Key = entry.s3Key;
  inMemoryProfileImageEntry.dataUrl = entry.dataUrl;
  syncLegacyProfilePicCache(entry);
};

const readStoredEntry = (): SessionProfileImageEntry => {
  if (!isBrowser()) {
    return inMemoryProfileImageEntry;
  }

  try {
    const raw = window.sessionStorage.getItem(SESSION_PROFILE_IMAGE_KEY);
    if (!raw) {
      return inMemoryProfileImageEntry;
    }

    const parsed = JSON.parse(raw) as Partial<SessionProfileImageEntry>;
    const entry: SessionProfileImageEntry = {
      userId: parsed.userId ?? null,
      s3Key: typeof parsed.s3Key === 'string' ? parsed.s3Key : null,
      dataUrl: typeof parsed.dataUrl === 'string' ? parsed.dataUrl : null,
    };

    setInMemoryEntry(entry);
    return entry;
  } catch {
    return inMemoryProfileImageEntry;
  }
};

const persistEntry = (entry: SessionProfileImageEntry) => {
  setInMemoryEntry(entry);

  if (!isBrowser()) {
    return;
  }

  try {
    window.sessionStorage.setItem(SESSION_PROFILE_IMAGE_KEY, JSON.stringify(entry));
  } catch {
  }
};

const blobToDataUrl = async (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read profile image blob.'));
    reader.readAsDataURL(blob);
  });
};

export const clearSessionProfileImage = () => {
  setInMemoryEntry({ userId: null, s3Key: null, dataUrl: null });

  if (!isBrowser()) {
    return;
  }

  window.sessionStorage.removeItem(SESSION_PROFILE_IMAGE_KEY);
};

export const getSessionProfileImage = (
  userId?: number | string | null,
  s3Key?: string | null,
): string => {
  if (!userId || !s3Key) {
    return '';
  }

  const entry = readStoredEntry();
  if (entry.userId === userId && entry.s3Key === s3Key && entry.dataUrl) {
    return entry.dataUrl;
  }

  return '';
};

export const cacheSessionProfileImageDataUrl = (
  userId: number | string,
  s3Key: string,
  dataUrl: string,
): string => {
  persistEntry({ userId, s3Key, dataUrl });
  return dataUrl;
};

export const cacheSessionProfileImageBlob = async (
  userId: number | string,
  s3Key: string,
  blob: Blob,
): Promise<string> => {
  const dataUrl = await blobToDataUrl(blob);
  return cacheSessionProfileImageDataUrl(userId, s3Key, dataUrl);
};

export const hydrateSessionProfileImage = async (
  userId?: number | string | null,
  s3Key?: string | null,
): Promise<string> => {
  if (!userId || !s3Key) {
    clearSessionProfileImage();
    return '';
  }

  const cachedImage = getSessionProfileImage(userId, s3Key);
  if (cachedImage) {
    return cachedImage;
  }

  const signedUrl = await getProfilePicDownloadURL(s3Key);
  const response = await fetch(signedUrl);
  if (!response.ok) {
    throw new Error('Failed to fetch profile image.');
  }

  const blob = await response.blob();
  return cacheSessionProfileImageBlob(userId, s3Key, blob);
};